"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

type Slide = {
  src: string; // "/hero/slide-1.jpg"
  alt: string;
};

export default function HeroCarousel({
  locale,
  eyebrow,
  titleLine1,
  titleLine2,
  paragraph,
  ctaPrimary,
  ctaSecondary,
  hrefPrimary,
  hrefSecondary,
  slides,
  intervalMs = 7000,
}: {
  locale: "fr" | "en";
  eyebrow: string;
  titleLine1: string;
  titleLine2: string;
  paragraph: string;
  ctaPrimary: string;
  ctaSecondary: string;
  hrefPrimary: string;
  hrefSecondary: string;
  slides: Slide[];
  intervalMs?: number;
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<number | null>(null);

  const safeSlides = useMemo(() => (slides?.length ? slides : []), [slides]);
  const active = safeSlides.length ? safeSlides[index % safeSlides.length] : null;

  useEffect(() => {
    if (!safeSlides.length) return;

    if (timerRef.current) window.clearInterval(timerRef.current);
    if (paused) return;

    timerRef.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % safeSlides.length);
    }, intervalMs);

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [safeSlides.length, intervalMs, paused]);

  const go = (dir: -1 | 1) => {
    if (!safeSlides.length) return;
    setIndex((i) => (i + dir + safeSlides.length) % safeSlides.length);
  };

  if (!active) return null;

  return (
    <section
      className="relative overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[88vh] min-h-[640px] w-full">
        <AnimatePresence mode="sync">
          <motion.div
            key={active.src}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <motion.div
              className="absolute inset-0"
            >
              <Image
                src={active.src}
                alt={active.alt}
                fill
                priority
                className="object-cover"
                sizes="100vw"
              />
            </motion.div>

            {/* Overlays luxe */}
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/5 to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 mx-auto flex h-full max-w-6xl items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
          <p className="font-body font-light uppercase tracking-[0.35em] text-white/60 text-xs md:text-sm">
            {eyebrow}
          </p>

            <h1 className="font-heading text-5xl md:text-7xl leading-[1.05] font-light tracking-tight text-white">
            {titleLine1} <br />
            <span className="italic">{titleLine2}</span>
            </h1>

            <p className="font-body font-light text-lg md:text-xl text-white/75 leading-relaxed">
            {paragraph}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href={hrefPrimary}
                className="inline-flex rounded-full bg-white px-6 py-3 text-xs tracking-[0.18em] uppercase text-black hover:opacity-90 transition-opacity"
              >
                {ctaPrimary}
              </a>

              <a
                href={hrefSecondary}
                className="inline-flex rounded-full border border-white/20 bg-white/5 px-6 py-3 text-xs tracking-[0.18em] uppercase text-white hover:bg-white/10 transition-colors"
              >
                {ctaSecondary}
              </a>
            </div>

            <div className="mt-10 text-[11px] tracking-[0.18em] uppercase text-white/45">
              {locale === "fr" ? "Survol = pause" : "Hover to pause"}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="pointer-events-none absolute inset-x-0 bottom-8 z-20">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="pointer-events-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => go(-1)}
                className="rounded-full border border-white/20 bg-black/40 px-4 py-2 text-xs tracking-[0.18em] uppercase text-white/90 backdrop-blur hover:bg-black/55 transition"
                aria-label="Previous slide"
              >
                ←
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                className="rounded-full border border-white/20 bg-black/40 px-4 py-2 text-xs tracking-[0.18em] uppercase text-white/90 backdrop-blur hover:bg-black/55 transition"
                aria-label="Next slide"
              >
                →
              </button>
            </div>

            <div className="pointer-events-auto flex items-center gap-2">
              {safeSlides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={[
                    "h-2 w-2 rounded-full transition",
                    i === index ? "bg-white" : "bg-white/35 hover:bg-white/60",
                  ].join(" ")}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}